/**
 * CaribbeanLeaf Cannabis POS - Application Root
 *
 * Provider hierarchy:
 * 1. ErrorBoundary - Catch unhandled errors
 * 2. QueryProvider - React Query for data fetching
 * 3. AuthProvider - Authentication state
 * 4. BrowserRouter - Client-side routing
 * 5. AppShell - Layout wrapper (when authenticated)
 */

import { ReactNode } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { QueryProvider } from '@/providers/QueryProvider';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { CustomerAuthProvider } from '@/hooks/useCustomerAuth';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { Toaster } from '@/components/ui/toaster';
import { PWAPrompts } from '@/components/pwa/PWAPrompts';
import { routes } from '@/routes';
import { customerRoutes } from '@/routes/customer-routes';
import { CUSTOMER_BASE } from '@/config/customer';

interface RouteConfig {
  path?: string;
  element?: ReactNode;
  children?: RouteConfig[];
}

/**
 * Renders route tree from a route config array.
 */
function renderRoutes(routeConfig: RouteConfig[]) {
  return routeConfig.map((route, index) => {
    if (route.children) {
      return (
        <Route key={index} element={route.element}>
          {route.children.map((child: RouteConfig, childIndex: number) => {
            if (child.children) {
              return (
                <Route key={childIndex} element={child.element}>
                  {child.children.map((grandchild: RouteConfig, gcIndex: number) => (
                    <Route
                      key={gcIndex}
                      path={grandchild.path}
                      element={grandchild.element}
                    />
                  ))}
                </Route>
              );
            }
            return (
              <Route
                key={childIndex}
                path={child.path}
                element={child.element}
              />
            );
          })}
        </Route>
      );
    }
    return <Route key={index} path={route.path} element={route.element} />;
  });
}

/**
 * Routes for the application.
 *
 * Two separate portals with independent auth:
 * - /customer/* → Customer portal (CustomerAuthProvider, CustomerLayout)
 * - everything else → Staff POS portal (AuthProvider, AppShell)
 */
function AppRoutes() {
  const location = useLocation();
  const isCustomerPage = location.pathname.startsWith(CUSTOMER_BASE);

  // Customer portal - separate auth provider, no AppShell
  if (isCustomerPage) {
    return (
      <CustomerAuthProvider>
        <Routes>{renderRoutes(customerRoutes)}</Routes>
      </CustomerAuthProvider>
    );
  }

  // Staff portal - existing logic unchanged
  return <StaffRoutes />;
}

/**
 * Staff POS portal routes with AppShell wrapper.
 */
function StaffRoutes() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const isLoginPage = location.pathname === '/login';
  const showShell = isAuthenticated && !isLoginPage;

  const content = <Routes>{renderRoutes(routes)}</Routes>;

  return showShell ? <AppShell>{content}</AppShell> : content;
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster />
            <PWAPrompts />
          </BrowserRouter>
        </AuthProvider>
      </QueryProvider>
    </ErrorBoundary>
  );
}
